/**
 * UserProvider — Apollo → Zustand store bridge.
 *
 * Calls useMeQuery and useSignupMemberMutation (Apollo hooks that must stay
 * in a React component), computes all derived booleans, and syncs the full
 * result to useMeStore synchronously during render.
 *
 * Why render-time sync instead of useLayoutEffect:
 *   React flushes layout effects bottom-up, so children run their own effects
 *   before the parent's useLayoutEffect fires. Writing to the store during the
 *   render body (before children render) means every descendant sees correct
 *   data on its first render — no "wrong first render" window.
 */
import React, {FC, useCallback, useLayoutEffect, useMemo, useRef} from 'react'

import {Dinero} from 'dinero.js'

import {
  MeDocument,
  User,
  useMeQuery,
  useSignupMemberMutation,
} from '@progyny/generated/graphql'
import {MeUser, useMeStore} from '@progyny/store/meStore'
import {FERTILITY} from '@progyny/utils/constants'
import {evaluateProfileForProductLine} from '@progyny/utils/evaluateProfile'

import {CONSERVATIVE_COUNTRIES} from '../const'

// ── UserProvider ──────────────────────────────────────────────────────────────

type Props = {children: React.ReactNode}

export const UserProvider: FC<Props> = React.memo(({children}) => {
  const {data, loading, error, refetch} = useMeQuery({
    notifyOnNetworkStatusChange: false,
  })
  const [signupEmployeeMutation] = useSignupMemberMutation()

  const user = useMemo(() => (data?.me as User) || undefined, [data])

  const activeProductLine = useMemo(
    () => user?.member?.activeMemberJourney || FERTILITY,
    [user?.member?.activeMemberJourney]
  )

  const countryCode =
    user?.member?.countryOfEmployment?.country?.code || undefined

  const evaluateProfile = useCallback(
    (productLine: string) =>
      evaluateProfileForProductLine(user?.member, productLine),
    [user?.member]
  )

  const signupEmployee = useCallback(
    (variables: any) =>
      signupEmployeeMutation({
        variables,
        refetchQueries: [{query: MeDocument}],
        awaitRefetchQueries: true,
      }),
    [signupEmployeeMutation]
  )

  const getMyLocale = useCallback(() => window.navigator.language, [])

  const setMyLocale = useCallback(
    (dinero: Dinero) => dinero.setLocale(getMyLocale()),
    [getMyLocale]
  )

  const isInConservativeCountry = useCallback(
    // @ts-ignore
    () => CONSERVATIVE_COUNTRIES.indexOf(countryCode) > -1,
    [countryCode]
  )

  const logout = useCallback(() => {}, [])

  // ── Derived values (computed during render) ───────────────────────────────

  const isLoggedIn = user !== undefined
  const isEmailConfirmed = isLoggedIn && !!user?.isEmailConfirmed
  const hasAcceptedTerms = Boolean(
    user?.member?.acceptedPrivacyStatement &&
    user?.member?.acceptedTermsConditions
  )
  const isAccountCreated =
    hasAcceptedTerms && !!user?.member?.city && !!user?.member?.birthDate
  const isProfileSetup = evaluateProfile(activeProductLine)
  const isAuthorized = isAccountCreated && isProfileSetup
  const isSsoLogin = (user?.member?.isSsoLogin as boolean) ?? false
  const privateEmailChangePending =
    (user?.member?.privateEmailChangePending as boolean) ?? false
  const companyHasBudget =
    user?.member?.contract?.hasBudget === undefined
      ? true
      : (user?.member?.contract.hasBudget as boolean)
  const isTestCompany = (user?.company?.isTestCompany as boolean) ?? false
  const mainEmail = user?.email
  const journeyCommunicationEmail = user?.journeyCommunicationEmail
  const companyName = user?.company?.name || undefined
  const hasReimbursementRequests =
    user?.member?.hasReimbursementRequests || false
  const hasStartedLegacyOnboarding =
    user?.member?.hasStartedLegacyOnboarding || false

  // Onboarding data derived from user.member (object — memoised to keep stable ref)
  const onboardingData = useMemo(() => {
    if (!user?.member) return undefined
    return {
      acceptPrivacyStatement: Boolean(user.member.acceptedPrivacyStatement),
      acceptTermsConditions: Boolean(user.member.acceptedTermsConditions),
      birthDate: user.member.birthDate,
      city: user.member.city,
      isAspiring: user.member.isAspiring || false,
      isEligible: user.member.isEligible || false,
      preferredGivenName: user.member.preferredGivenName,
      preferredSurname: user.member.preferredSurname,
      privateEmail: user.member.privateEmail,
      pronouns: user.member.pronouns,
      birthAssignedSex: user.member.birthAssignedSex,
      genderIdentity: user.member.genderIdentity,
      familyFormingJourney: user.member.familyFormingJourney,
      relationshipDynamic: user.member.relationshipDynamic,
      lastMenstrualPeriod: user.member.menopauseJourney?.lastMenstrualPeriod,
      menopauseSymptoms: user.member.menopauseJourney?.menopauseSymptoms,
      menstrualPeriodStatus:
        user.member.menopauseJourney?.menstrualPeriodStatus,
      otherSymptomsDescription:
        user.member.menopauseJourney?.otherSymptomsDescription,
      pregnancyPostpartumStatus:
        user.member.pregnancyPostpartumJourney?.pregnancyPostpartumStatus,
      dueDate: user.member.pregnancyPostpartumJourney?.dueDate,
      deliveryDate: user.member.pregnancyPostpartumJourney?.deliveryDate,
    }
  }, [user?.member])

  // ── Store sync — two-phase strategy ─────────────────────────────────────
  //
  // Phase 1 (render-time, initial publish only):
  //   On the very first render no descendant is mounted yet, so writing to
  //   the store synchronously gives children the correct values on their own
  //   first render without a "wrong first render" flash.
  //   `prevDepsRef.current.user === null` is true exactly once.
  //
  // Phase 2 (useLayoutEffect, all subsequent updates):
  //   After mount, Apollo data changes (refetch, cache writes, mutation
  //   refetchQueries) are applied here. useLayoutEffect fires before paint
  //   but after commit, avoiding the render-phase mutation risk that
  //   concurrent rendering (startTransition in this codebase) can expose:
  //   a thrown-away render would advance prevDepsRef but not commit, causing
  //   the guard and the committed tree to disagree.

  const prevDepsRef = useRef<{user: unknown; loading: boolean; error: unknown}>(
    {
      user: null, // null sentinel: "Phase 1 not yet run"; user is never null after store change
      loading: true,
      error: undefined,
    }
  )

  // Build the full store patch inline so both phases share the same shape
  // without introducing a render-body function that would need to be in deps.
  const patch = {
    user: user as MeUser | undefined,
    loading,
    error,
    refetch,
    signupEmployee,
    evaluateProfileForProductLine: evaluateProfile,
    getMyLocale,
    setMyLocale,
    isInConservativeCountry,
    logout,
    isLoggedIn,
    isEmailConfirmed,
    hasAcceptedTerms,
    isAccountCreated,
    isProfileSetup,
    isAuthorized,
    isSsoLogin,
    privateEmailChangePending,
    companyHasBudget,
    isTestCompany,
    mainEmail,
    journeyCommunicationEmail,
    countryCode,
    companyName,
    hasReimbursementRequests,
    hasStartedLegacyOnboarding,
    ...(onboardingData !== undefined && {onboardingData}),
  }

  // Phase 1 — initial publish only (render-time, safe: no subscriber exists yet)
  if (prevDepsRef.current.user === null) {
    prevDepsRef.current = {user, loading, error}
    useMeStore.getState().setMeData(patch)
  }

  // Phase 2 — steady-state updates (useLayoutEffect, post-commit, pre-paint)
  useLayoutEffect(() => {
    if (
      prevDepsRef.current.user === user &&
      prevDepsRef.current.loading === loading &&
      prevDepsRef.current.error === error
    ) {
      return // nothing changed — avoid a redundant Zustand notification
    }
    prevDepsRef.current = {user, loading, error}
    useMeStore.getState().setMeData({
      user: user as MeUser | undefined,
      loading,
      error,
      refetch,
      signupEmployee,
      evaluateProfileForProductLine: evaluateProfile,
      getMyLocale,
      setMyLocale,
      isInConservativeCountry,
      logout,
      isLoggedIn,
      isEmailConfirmed,
      hasAcceptedTerms,
      isAccountCreated,
      isProfileSetup,
      isAuthorized,
      isSsoLogin,
      privateEmailChangePending,
      companyHasBudget,
      isTestCompany,
      mainEmail,
      journeyCommunicationEmail,
      countryCode,
      companyName,
      hasReimbursementRequests,
      hasStartedLegacyOnboarding,
      ...(onboardingData !== undefined && {onboardingData}),
    })
  }, [
    user,
    loading,
    error,
    refetch,
    signupEmployee,
    evaluateProfile,
    getMyLocale,
    setMyLocale,
    isInConservativeCountry,
    logout,
    activeProductLine,
    countryCode,
    onboardingData,
    isLoggedIn,
    isEmailConfirmed,
    hasAcceptedTerms,
    isAccountCreated,
    isProfileSetup,
    isAuthorized,
    isSsoLogin,
    privateEmailChangePending,
    companyHasBudget,
    isTestCompany,
    mainEmail,
    journeyCommunicationEmail,
    companyName,
    hasReimbursementRequests,
    hasStartedLegacyOnboarding,
  ])

  return <>{children}</>
})
